import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import API from "../../api/axiosConfig";
import { toast } from "react-toastify";
import { motion, AnimatePresence } from "framer-motion";
import {
  Gavel, Plus, Clock, TrendingUp, Eye, Trash2, Edit2,
  CheckCircle2, X, Trophy, Bot, ImagePlus, Loader2,
} from "lucide-react";
import dayjs from "dayjs";
import "dayjs/locale/es";

dayjs.locale("es");

const BASE_URL = (process.env.REACT_APP_API_URL?.replace(/\/api\/?$/, "") || "http://localhost:4000").replace(/\/$/, "");

function StatusBadge({ status }) {
  const map = {
    draft:     { label: "Borrador",   cls: "bg-gray-100 text-gray-500"          },
    scheduled: { label: "Programada", cls: "bg-blue-100 text-blue-700"           },
    active:    { label: "En vivo 🔴", cls: "bg-green-100 text-green-700"         },
    ended:     { label: "Finalizada", cls: "bg-orange-100 text-orange-700"       },
    sold:      { label: "Vendida ✓",  cls: "bg-supernova-100 text-supernova-700" },
    cancelled: { label: "Cancelada",  cls: "bg-red-100 text-red-600"             },
  };
  const s = map[status] ?? map.draft;
  return <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${s.cls}`}>{s.label}</span>;
}

const EMPTY_FORM = {
  title: "", description: "", images: [],
  startingPrice: "", minIncrement: "1",
  reservePrice: "", buyNowPrice: "",
  startAt: "", endAt: "",
  antiSnipeMin: "5", antiSnipeExt: "5",
};

export default function CompanyAuctions() {
  const [auctions,      setAuctions]      = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [showForm,      setShowForm]      = useState(false);
  const [form,          setForm]          = useState(EMPTY_FORM);
  const [editId,        setEditId]        = useState(null);
  const [saving,        setSaving]        = useState(false);
  const [uploadingImg,  setUploadingImg]  = useState(false);
  const [editStatus,    setEditStatus]    = useState(null); // status of auction being edited
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const user = JSON.parse(localStorage.getItem("user") || "null");
      if (!user?.companyId) return;
      const res = await API.get(`/auctions?companyId=${user.companyId}&status=draft,scheduled,active,ended,sold,cancelled&limit=100`);
      setAuctions(res.data?.data?.auctions ?? []);
    } catch {
      toast.error("Error cargando subastas");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditId(null);
    setEditStatus(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  };

  const openEdit = (a) => {
    setEditId(a.id);
    setEditStatus(a.status);
    setForm({
      title:         a.title,
      description:   a.description ?? "",
      images:        a.images ?? [],
      startingPrice: Number(a.startingPrice).toString(),
      minIncrement:  Number(a.minIncrement).toString(),
      reservePrice:  a.reservePrice ? Number(a.reservePrice).toString() : "",
      buyNowPrice:   a.buyNowPrice  ? Number(a.buyNowPrice).toString()  : "",
      startAt:       dayjs(a.startAt).format("YYYY-MM-DDTHH:mm"),
      endAt:         dayjs(a.endAt).format("YYYY-MM-DDTHH:mm"),
      antiSnipeMin:  a.antiSnipeMin.toString(),
      antiSnipeExt:  a.antiSnipeExt.toString(),
    });
    setShowForm(true);
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    if (form.images.length + files.length > 5) {
      toast.warn("Máximo 5 imágenes por subasta");
      return;
    }
    setUploadingImg(true);
    try {
      const data = new FormData();
      files.forEach((f) => data.append("images", f));
      const res = await API.post("/auctions/upload-images", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const urls = res.data?.data?.images ?? [];
      setForm((prev) => ({ ...prev, images: [...prev.images, ...urls] }));
      toast.success(`${urls.length} imagen(es) subida(s)`);
    } catch {
      toast.error("Error al subir imágenes");
    } finally {
      setUploadingImg(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeImage = (idx) => {
    setForm((prev) => ({ ...prev, images: prev.images.filter((_, i) => i !== idx) }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const isActive = editStatus === "active";
    if (!form.title || (!isActive && (!form.startingPrice || !form.startAt || !form.endAt))) {
      toast.warn("Completa los campos obligatorios");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        startingPrice: Number(form.startingPrice),
        minIncrement:  Number(form.minIncrement),
        reservePrice:  form.reservePrice ? Number(form.reservePrice) : undefined,
        buyNowPrice:   form.buyNowPrice  ? Number(form.buyNowPrice)  : undefined,
        antiSnipeMin:  Number(form.antiSnipeMin),
        antiSnipeExt:  Number(form.antiSnipeExt),
      };
      if (editId) {
        await API.put(`/auctions/${editId}`, payload);
        toast.success("Subasta actualizada");
      } else {
        await API.post("/auctions", payload);
        toast.success("Subasta creada");
      }
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.msg || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async (id) => {
    if (!window.confirm("¿Cancelar esta subasta?")) return;
    try {
      await API.patch(`/auctions/${id}/cancel`);
      toast.success("Subasta cancelada");
      load();
    } catch (err) {
      toast.error(err.response?.data?.msg || "Error");
    }
  };

  return (
    <div className="w-full max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Gavel className="text-supernova-500" size={24} /> Subastas
          </h2>
          <p className="text-gray-500 text-sm mt-0.5">Gestiona tus subastas en tiempo real</p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 bg-supernova-400 hover:bg-supernova-500 text-black font-bold px-4 py-2.5 rounded-xl transition text-sm"
        >
          <Plus size={16} /> Nueva subasta
        </button>
      </div>

      {/* Lista */}
      {loading ? (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => <div key={i} className="bg-white rounded-2xl h-24 animate-pulse" />)}
        </div>
      ) : auctions.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-2xl border border-dashed border-gray-200">
          <Gavel size={40} className="mx-auto text-gray-200 mb-3" />
          <p className="text-gray-500 font-semibold">No tienes subastas aún</p>
          <p className="text-gray-400 text-sm mt-1">Crea tu primera subasta y vende al mejor postor</p>
          <button onClick={openCreate} className="mt-4 bg-supernova-400 hover:bg-supernova-500 text-black font-bold px-5 py-2.5 rounded-xl text-sm transition">
            Crear subasta
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {auctions.map((a, i) => (
            <motion.div
              key={a.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.04 }}
              className="bg-white rounded-2xl shadow p-4 flex items-center gap-4"
            >
              {/* Imagen */}
              <div className="w-16 h-16 rounded-xl bg-gray-100 flex-shrink-0 overflow-hidden">
                {a.images?.[0]
                  ? <img src={`${BASE_URL}/${a.images[0]}`} alt="" className="w-full h-full object-cover" />
                  : <div className="w-full h-full flex items-center justify-center"><Gavel size={20} className="text-gray-300" /></div>
                }
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <StatusBadge status={a.status} />
                  <p className="font-semibold text-gray-800 truncate text-sm">{a.title}</p>
                </div>
                <div className="flex items-center gap-4 text-xs text-gray-400">
                  <span className="flex items-center gap-1">
                    <TrendingUp size={10} /> {a.totalBids} pujas
                  </span>
                  <span className="font-bold text-supernova-600">${Number(a.currentPrice).toLocaleString("es-CO")}</span>
                  <span className="flex items-center gap-1">
                    <Clock size={10} /> {dayjs(a.endAt).format("DD/MM HH:mm")}
                  </span>
                  {a.winnerId && (
                    <span className="flex items-center gap-1 text-yellow-600">
                      <Trophy size={10} /> Vendida
                    </span>
                  )}
                </div>
              </div>

              {/* Acciones */}
              <div className="flex items-center gap-2 flex-shrink-0">
                <Link to={`/auction/${a.id}`} className="p-2 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-500 transition" title="Ver">
                  <Eye size={15} />
                </Link>
                {!["sold", "cancelled", "ended"].includes(a.status) && (
                  <button
                    onClick={() => openEdit(a)}
                    className="p-2 rounded-xl bg-blue-50 hover:bg-blue-100 text-blue-600 transition"
                    title={a.status === "active" ? "Editar fotos y descripción" : "Editar"}
                  >
                    <Edit2 size={15} />
                  </button>
                )}
                {!["sold", "cancelled", "ended"].includes(a.status) && (
                  <button onClick={() => handleCancel(a.id)} className="p-2 rounded-xl bg-red-50 hover:bg-red-100 text-red-500 transition" title="Cancelar">
                    <Trash2 size={15} />
                  </button>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* ── Modal de crear/editar ── */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={(e) => e.target === e.currentTarget && setShowForm(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            >
              {/* Header modal */}
              <div className="flex items-center justify-between p-6 border-b border-gray-100">
                <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                  <Gavel size={20} className="text-supernova-500" />
                  {editId ? "Editar subasta" : "Nueva subasta"}
                </h3>
                <button onClick={() => setShowForm(false)} className="p-2 rounded-xl hover:bg-gray-100 text-gray-400">
                  <X size={18} />
                </button>
              </div>

              <form onSubmit={handleSave} className="p-6 space-y-5">
                {/* Aviso subasta activa */}
                {editStatus === "active" && (
                  <div className="bg-orange-50 border border-orange-200 rounded-xl px-4 py-3 text-xs text-orange-700 font-semibold">
                    🔴 Subasta en curso — solo puedes editar el título, descripción y fotos.
                  </div>
                )}

                {/* Título */}
                <div>
                  <label className="text-xs font-semibold text-gray-500 block mb-1">Título *</label>
                  <input
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-supernova-400"
                    placeholder="Ej: iPhone 15 Pro — subasta exclusiva"
                  />
                </div>

                {/* Descripción */}
                <div>
                  <label className="text-xs font-semibold text-gray-500 block mb-1">Descripción</label>
                  <textarea
                    rows={3}
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-supernova-400 resize-none"
                    placeholder="Describe el artículo..."
                  />
                </div>

                {/* Precios + Fechas + Anti-snipe — ocultos si subasta activa */}
                {editStatus !== "active" && (
                  <>
                    {/* Precios */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs font-semibold text-gray-500 block mb-1">Precio inicial *</label>
                        <input type="number" step="0.01" min="0"
                          value={form.startingPrice}
                          onChange={(e) => setForm({ ...form, startingPrice: e.target.value })}
                          className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-supernova-400"
                          placeholder="50000" />
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-gray-500 block mb-1">Incremento mínimo *</label>
                        <input type="number" step="0.01" min="1"
                          value={form.minIncrement}
                          onChange={(e) => setForm({ ...form, minIncrement: e.target.value })}
                          className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-supernova-400"
                          placeholder="1000" />
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-gray-500 block mb-1">
                          Precio de reserva <span className="font-normal text-gray-400">(oculto)</span>
                        </label>
                        <input type="number" step="0.01" min="0"
                          value={form.reservePrice}
                          onChange={(e) => setForm({ ...form, reservePrice: e.target.value })}
                          className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-supernova-400"
                          placeholder="Opcional" />
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-gray-500 block mb-1">
                          Cómpralo ya <span className="font-normal text-gray-400">(opcional)</span>
                        </label>
                        <input type="number" step="0.01" min="0"
                          value={form.buyNowPrice}
                          onChange={(e) => setForm({ ...form, buyNowPrice: e.target.value })}
                          className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-supernova-400"
                          placeholder="Opcional" />
                      </div>
                    </div>

                    {/* Fechas */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs font-semibold text-gray-500 block mb-1">Fecha inicio *</label>
                        <input type="datetime-local"
                          value={form.startAt}
                          onChange={(e) => setForm({ ...form, startAt: e.target.value })}
                          className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-supernova-400" />
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-gray-500 block mb-1">Fecha fin *</label>
                        <input type="datetime-local"
                          value={form.endAt}
                          onChange={(e) => setForm({ ...form, endAt: e.target.value })}
                          className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-supernova-400" />
                      </div>
                    </div>

                    {/* Anti-snipe */}
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-3">
                      <p className="text-xs font-bold text-blue-700 flex items-center gap-1.5">
                        <Bot size={13} /> Protección anti-snipe
                      </p>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-xs text-blue-600 block mb-1">Activar si quedan (min)</label>
                          <input type="number" min="0"
                            value={form.antiSnipeMin}
                            onChange={(e) => setForm({ ...form, antiSnipeMin: e.target.value })}
                            className="w-full border border-blue-200 rounded-xl px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white" />
                        </div>
                        <div>
                          <label className="text-xs text-blue-600 block mb-1">Extender por (min)</label>
                          <input type="number" min="0"
                            value={form.antiSnipeExt}
                            onChange={(e) => setForm({ ...form, antiSnipeExt: e.target.value })}
                            className="w-full border border-blue-200 rounded-xl px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white" />
                        </div>
                      </div>
                      <p className="text-[10px] text-blue-500">
                        Si alguien puja en los últimos {form.antiSnipeMin} min, el tiempo se extiende {form.antiSnipeExt} min más.
                        Pon 0 para desactivar.
                      </p>
                    </div>
                  </>
                )}

                {/* Imágenes */}
                <div>
                  <label className="text-xs font-semibold text-gray-500 block mb-2">
                    Fotos del artículo <span className="font-normal text-gray-400">(máx. 5)</span>
                  </label>

                  {/* Thumbnails */}
                  {form.images.length > 0 && (
                    <div className="flex gap-2 flex-wrap mb-3">
                      {form.images.map((url, idx) => (
                        <div key={idx} className="relative w-20 h-20 rounded-xl overflow-hidden border border-gray-200 group">
                          <img src={url.startsWith("http") ? url : `${BASE_URL}/${url}`} alt="" className="w-full h-full object-cover" />
                          <button
                            type="button"
                            onClick={() => removeImage(idx)}
                            className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
                          >
                            <X size={11} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Upload button */}
                  {form.images.length < 5 && (
                    <>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        className="hidden"
                        onChange={handleImageUpload}
                      />
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploadingImg}
                        className="flex items-center gap-2 border-2 border-dashed border-gray-300 hover:border-supernova-400 text-gray-400 hover:text-supernova-600 text-sm font-semibold px-4 py-3 rounded-xl transition w-full justify-center disabled:opacity-60"
                      >
                        {uploadingImg
                          ? <><Loader2 size={15} className="animate-spin" /> Subiendo...</>
                          : <><ImagePlus size={15} /> Subir fotos</>
                        }
                      </button>
                    </>
                  )}
                </div>

                {/* Botones */}
                <div className="flex gap-3 pt-2">
                  <button type="button" onClick={() => setShowForm(false)}
                    className="flex-1 border border-gray-300 text-gray-600 font-semibold py-3 rounded-xl hover:bg-gray-50 transition text-sm">
                    Cancelar
                  </button>
                  <button type="submit" disabled={saving}
                    className="flex-1 bg-supernova-400 hover:bg-supernova-500 disabled:opacity-60 text-black font-bold py-3 rounded-xl transition flex items-center justify-center gap-2 text-sm">
                    {saving
                      ? <span className="animate-spin w-4 h-4 border-2 border-black border-t-transparent rounded-full" />
                      : <><CheckCircle2 size={16} /> {editId ? "Guardar cambios" : "Crear subasta"}</>
                    }
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
